import React, { useEffect, useReducer } from 'react';
import { produce } from 'immer';
import classNames from 'classnames';
import { Button, CheckBox, Search, Validator } from 'common/components';
import { modal } from 'layout';
import { getEddCountries, raCategoryMap } from '../../../data/data';
import { Data } from '../nationRisk/NationRisk';
import './Modal_RaNation.scss';

interface NationTableProps {
  nations: Nation[];
  className?: string;
  title: string;
  checkedLen: number;
  checkAllHandler: (checked: boolean) => void;
  children: (nation: Nation) => JSX.Element;
}
function NationTable(props: NationTableProps) {
  const { nations, title, children, checkedLen, checkAllHandler } = props;
  const checkAll = !!checkedLen && checkedLen === nations.length;
  return (
    <div className={classNames('nations__table', props.className)}>
      <div className="head">
        <h4>{title}</h4>
        <div>
          <CheckBox text="전체 선택" checked={checkAll ? 1 : 0} onChange={(check) => checkAllHandler(!!check)} />
          <span className={classNames({ disabled: !checkedLen })} onClick={() => checkAllHandler(false)}>
            선택 해제
          </span>
        </div>
      </div>
      <div className="content">
        {nations.length ? nations.map((nation) => children(nation)) : <div>선택된 국가가 없습니다</div>}
      </div>
    </div>
  );
}

type Action = { type: 'searchText'; payload: string } | { type: 'nations'; payload: Nation[] };
type Nation = Data & { selected: boolean; checked: boolean };
type State = { searchText: string; nations: Nation[] };

const initialState: State = {
  searchText: '',
  nations: [],
};

const reducer = (state: State, action: Action) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case 'searchText':
        draft['searchText'] = action.payload;
        break;
      case 'nations':
        draft['nations'] = action.payload;
        break;
      default:
        break;
    }
  });
};

interface Props {
  closeHandler: () => void;
  submitHandler: (nextNationData: Data[]) => void;
  eddCategoryId: number;
  item: Data[];
}
function Modal_RaNation(props: Props) {
  const { closeHandler, submitHandler, eddCategoryId, item } = props;
  const [state, setState] = useReducer(reducer, initialState);
  const raName = raCategoryMap[eddCategoryId as keyof typeof raCategoryMap];

  useEffect(() => {
    const nationsAll = getEddCountries({ warn_only: false });
    const nations = nationsAll.map((nation) => {
      if (item.filter((el) => el.country_id === nation.country_id).length) {
        return { ...nation, checked: false, selected: true };
      }
      return { ...nation, checked: false, selected: false };
    });

    setState({ type: 'nations', payload: nations });
  }, []);

  const onSubmit = () => {
    const selectedNations = state.nations.filter((nation) => nation.selected);
    submitHandler(selectedNations);
    closeHandler();
  };

  const searchHandler = (searchText: string) => {
    setState({ type: 'searchText', payload: searchText });
  };

  const checkAllHandler = (selected: boolean) => (checked: boolean) => {
    const nations = state.nations.map((nation) => (nation.selected === selected ? { ...nation, checked } : nation));
    setState({ type: 'nations', payload: nations });
  };

  const checkEachHandler = (country_id: number, checked: boolean) => {
    const nations = state.nations.map((nation) => (nation.country_id === country_id ? { ...nation, checked } : nation));
    setState({ type: 'nations', payload: nations });
  };

  const appendHandler = () => {
    const nations = state.nations.map((nation) =>
      !nation.selected && nation.checked ? { ...nation, selected: true, checked: false } : nation
    );
    setState({ type: 'nations', payload: nations });
  };

  const removeHandler = () => {
    const nations = state.nations.map((nation) =>
      nation.selected && nation.checked ? { ...nation, selected: false, checked: false } : nation
    );
    setState({ type: 'nations', payload: nations });
  };

  const filteredNations = state.nations.filter((nation) => nation.name_kr.includes(state.searchText));

  const availableNations = filteredNations.filter((nation) => !nation.selected);
  const selectedNations = filteredNations.filter((nation) => nation.selected);
  const availableNationsCheckedLen = availableNations.filter((nation) => nation.checked).length;
  const selectedNationsCheckedLen = selectedNations.filter((nation) => nation.checked).length;

  return (
    <Validator.Provider onSubmit={onSubmit}>
      <modal.Container id="modal-ra-item-nations-setting" styles={{ width: 1016 }}>
        <modal.Header>선택지 설정</modal.Header>
        <modal.Content>
          <div className="condition">
            <div className="item">
              <h4>위험 종류</h4>
              {raName}
            </div>
            <div className="item">
              <h4>항목</h4>
              국적
            </div>
          </div>
          <div className="searchbox__filter">
            <h4>검색</h4>
            <Search submitHandler={searchHandler} styles={{ width: '100%' }} onChange={searchHandler} />
          </div>
          <div className="nations__container">
            <NationTable
              nations={availableNations}
              className="available"
              title="선택 가능"
              checkedLen={availableNationsCheckedLen}
              checkAllHandler={checkAllHandler(false)}
            >
              {(nation) => (
                <div key={`${nation.iso_code3}`} className={classNames('nation', { checked: nation.checked })}>
                  <span>
                    <CheckBox
                      checked={nation.checked ? 1 : 0}
                      onChange={(check) => checkEachHandler(nation.country_id, !!check)}
                      text={nation.name_kr}
                    />
                  </span>
                  <span>
                    {nation.warn_labels.map((la, index) => (
                      <span key={`${la}_${index}`}>{la}</span>
                    ))}
                  </span>
                </div>
              )}
            </NationTable>
            <div className="control">
              <Button type="button" className="append" disabled={!availableNationsCheckedLen} onClick={appendHandler}>
                <>
                  추가
                  <i />
                </>
              </Button>
              <Button type="button" className="remove" disabled={!selectedNationsCheckedLen} onClick={removeHandler}>
                <>
                  삭제
                  <i />
                </>
              </Button>
            </div>
            <NationTable
              nations={selectedNations}
              className="selected"
              title="선택 됨"
              checkedLen={selectedNationsCheckedLen}
              checkAllHandler={checkAllHandler(true)}
            >
              {(nation) => (
                <div key={`${nation.iso_code3}`} className={classNames('nation', { checked: nation.checked })}>
                  <span>
                    <CheckBox
                      checked={nation.checked ? 1 : 0}
                      onChange={(check) => checkEachHandler(nation.country_id, !!check)}
                      text={nation.name_kr}
                    />
                  </span>
                  <span>
                    {nation.warn_labels.map((la, index) => {
                      return <span key={`${la}_${index}`}>{la}</span>;
                    })}
                  </span>
                </div>
              )}
            </NationTable>
          </div>
          <div className="info">※ 라벨이 포함된 국가는 삭제를 권장드리지 않습니다.</div>
        </modal.Content>
        <modal.Footer disabled={!selectedNations.length} closeHandler={closeHandler} submitText="설정" />
      </modal.Container>
    </Validator.Provider>
  );
}

export default Modal_RaNation;
